import { useEffect, useRef, useState } from 'react';
import { RoomType } from './RoomType';
import {
	BuildingFunction,
	ComputedUnit,
	GeometryDataItem,
	parsePath,
} from '@/services/forma/forma-helpers';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';
import { unitConversionService as unitService } from '@/services/unit-conversion.service';
import SpaceCard from './SpaceCard';
import MetricBox from './MetricBox';
import styles from './styles.module.scss';
import { WorkflowStage } from '@/types/palette';
import { RoomData } from './SpacePlanningTypes';

interface SpacePlanningProps {
	formaData: GeometryDataItem[];
	currentStage?: WorkflowStage;
}

export const SpacePlanning: React.FC<SpacePlanningProps> = ({ formaData, currentStage }) => {
	const [openCards, setOpenCards] = useState<Record<string, boolean>>({});
	const { spaces, updateSpace, refresh } = useSpacePlanningStore();
	const initialHydrationDoneRef = useRef(false);

	useEffect(() => {
		if (initialHydrationDoneRef.current) return;
		initialHydrationDoneRef.current = true;

		// Only fetch from storage if there is no local space data yet.
		if (Object.keys(spaces || {}).length === 0) {
			refresh();
		}
	}, [refresh, spaces]);

	const getPath = (item: GeometryDataItem, fn: BuildingFunction) => {
		const { building } = parsePath(item.path); // remove floor number from path
		return `${building}-${fn.functionId}`;
	};

	const handleTareChange = (key: string, value: number) => {
		const currentSpace = spaces[key] || { rooms: [] };
		updateSpace(key, { ...currentSpace, tare: value });
	};

	const calculateUsedAreaFromRooms = (rooms: RoomData[] = []): number =>
		rooms.reduce((acc, room) => acc + room.ASF * room.quantity, 0);

	const formatAreaTypeName = (raw: string) => {
		return raw
			.replace(/_/g, ' ')
			.toLowerCase()
			.replace(/\b\w/g, (l) => l.toUpperCase());
	};

	const formatRoomTypeLabel = (areaType: string, floorArea: number, level?: number) => {
		const name = formatAreaTypeName(areaType);
		const formattedArea = unitService.formatWithPreferredUnit(floorArea, 'area', 'm2');
		if (typeof level === 'number') {
			return `Level ${level} - ${name} (${formattedArea})`;
		}
		return `${name} (${formattedArea})`;
	};

	const summarizeByLevel = (areas: ComputedUnit[] = []) => {
		const grouped = new Map<number, number>();
		areas.forEach((area) => {
			const rawLevel = Number(area.level ?? 0);
			const level = rawLevel + 1;
			const current = grouped.get(level) ?? 0;
			grouped.set(level, current + (area.floorArea || 0));
		});

		return Array.from(grouped.entries())
			.sort((a, b) => a[0] - b[0])
			.map(([level, area]) => ({ level, area }));
	};

	const mapRoomTypes = (
		areas: ComputedUnit[] = [],
		totalASF: number = 0,
		includeLevelInLabel = false
	) => {
		const uniqueLevels = Array.from(
			new Set(areas.map((area) => Number(area.level ?? 0) + 1))
		).sort((a, b) => a - b);
		const otherOptions =
			includeLevelInLabel && uniqueLevels.length > 0
				? uniqueLevels.map((level) => ({
						id: `other-${level}`,
						label: `Level ${level} - Other`,
						value: 0,
						totalASF: 0,
						wallAreaTotal: 0,
						height: 0,
						level,
					}))
				: [
						{
							id: 'other',
							label: 'Other',
							value: 0,
							totalASF: 0,
							wallAreaTotal: 0,
							height: 0,
							level: undefined,
						},
					];

		return [
			{ id: '', label: '...', value: 0, totalASF: 0, wallAreaTotal: 0, height: 0 },
			...areas.map((area) => ({
				id: area.id, // @note: This ID is temporary and is used to help get other data from the list
				label: formatRoomTypeLabel(
					area.areaType,
					area.floorArea,
					includeLevelInLabel ? Number(area.level ?? 0) + 1 : undefined
				),
				value: area.floorArea,
				totalASF: Math.round(totalASF),
				wallAreaTotal: area.wallAreaTotal,
				height: area.height,
				level: Number(area.level ?? 0) + 1,
			})),
			...otherOptions,
		];
	};

	type GroupedFunctionCard = {
		path: string;
		functionId: string;
		functionName: string;
		areas: ComputedUnit[];
		valueTotal: number;
	};

	const groupFunctionCards = (items: GeometryDataItem[]): GroupedFunctionCard[] => {
		const grouped = new Map<
			string,
			{
				path: string;
				functionId: string;
				functionName: string;
				valueTotal: number;
				areaByKey: Map<string, ComputedUnit>;
			}
		>();

		items.forEach((item) => {
			item.functions.forEach((fn) => {
				const path = getPath(item, fn);
				const cardKey = `${path}::${fn.functionId}`;
				const current = grouped.get(cardKey);
				if (!current) {
					const areaByKey = new Map<string, ComputedUnit>();
					(fn.areas || []).forEach((area) => {
						const areaKey = `${area.id}-${area.level}-${area.spaceId}`;
						areaByKey.set(areaKey, area);
					});

					grouped.set(cardKey, {
						path,
						functionId: fn.functionId,
						functionName: fn.functionName,
						valueTotal: fn.value || 0,
						areaByKey,
					});
					return;
				}

				current.valueTotal += fn.value || 0;
				if (fn.functionName) {
					current.functionName = fn.functionName;
				}
				(fn.areas || []).forEach((area) => {
					const areaKey = `${area.id}-${area.level}-${area.spaceId}`;
					if (!current.areaByKey.has(areaKey)) {
						current.areaByKey.set(areaKey, area);
					}
				});
			});
		});

		return Array.from(grouped.values()).map((entry) => ({
			path: entry.path,
			functionId: entry.functionId,
			functionName: entry.functionName,
			valueTotal: entry.valueTotal,
			areas: Array.from(entry.areaByKey.values()),
		}));
	};

	type LevelCapacity = {
		level: number;
		grossArea: number;
		netArea: number;
		usedArea: number;
		remainingArea: number;
	};

	const getLevelCapacities = (areas: ComputedUnit[] = [], rooms: RoomData[] = [], tare = 0.15) => {
		const byLevel = new Map<number, number>();
		areas.forEach((area) => {
			const level = Number(area.level ?? 0) + 1;
			byLevel.set(level, (byLevel.get(level) ?? 0) + (area.floorArea || 0));
		});

		const usedByLevel = new Map<number, number>();
		rooms.forEach((room) => {
			if (typeof room.level !== 'number') return;
			usedByLevel.set(
				room.level,
				(usedByLevel.get(room.level) ?? 0) + room.ASF * room.quantity
			);
		});

		return Array.from(byLevel.entries())
			.sort((a, b) => a[0] - b[0])
			.map(([level, grossArea]) => {
				const netArea = grossArea - grossArea * tare;
				const usedArea = usedByLevel.get(level) ?? 0;
				const remainingArea = netArea - usedArea;
				return {
					level,
					grossArea,
					netArea,
					usedArea,
					remainingArea,
				} satisfies LevelCapacity;
			});
	};

	const handleToggleSpaceCard = (path: string) => {
		setOpenCards((prevOpenCards) => ({
			...prevOpenCards,
			[path]: !prevOpenCards[path],
		}));
	};
	const canEditRooms =
		currentStage === 'schematic' || currentStage === 'design_development';
	const isPredesignStage =
		currentStage === 'predesign_programming' || typeof currentStage === 'undefined';
	const groupedCards = groupFunctionCards(formaData);

	return (
		<>
			{groupedCards.map((card) => {
				const path = card.path;
				const roomsForPathAll = spaces[path]?.rooms || [];
				const tareValue = spaces[path]?.tare ?? 0.15;
				const grossAreaFromLevels = card.areas.reduce(
					(sum, area) => sum + (area.floorArea || 0),
					0
				);
				const grossFloorArea =
					grossAreaFromLevels > 0 ? grossAreaFromLevels : card.valueTotal;
				const levelSummaries = summarizeByLevel(card.areas);
				const visibleLevels = Array.from(
					new Set(levelSummaries.map((entry) => Number(entry.level)))
				);
				const hasMultipleLevels = visibleLevels.length > 1;
				const includeLevelInRoomTypeLabel = hasMultipleLevels;
				const roomTypes = mapRoomTypes(
					card.areas,
					grossFloorArea,
					includeLevelInRoomTypeLabel
				);
				const roomsForPath = roomsForPathAll.filter(
					(room) =>
						typeof room.level === 'number' && visibleLevels.includes(Number(room.level))
				);
				const usedArea = calculateUsedAreaFromRooms(roomsForPath);
				const levelCapacities = getLevelCapacities(card.areas, roomsForPath, tareValue);
				const assignedLevelUsed = levelCapacities.reduce(
					(sum, levelCapacity) => sum + levelCapacity.usedArea,
					0
				);
				const unassignedLevelUsed = Math.max(0, usedArea - assignedLevelUsed);
				const overCapacityLevels = levelCapacities.filter(
					(entry) => entry.remainingArea < 0
				);
				const hasSingleLevel = levelSummaries.length === 1;
				const showLevelBreakdown = isPredesignStage
					? levelSummaries.length > 0
					: levelSummaries.length > 1;
				const useBasicGate1Breakdown = isPredesignStage && hasSingleLevel;
				const title = hasSingleLevel
					? `${card.functionName} - Level ${levelSummaries[0].level}`
					: card.functionName;

				return (
					<div key={`${path}-${card.functionId}`}>
						<SpaceCard
							title={title}
							grossArea={grossFloorArea}
							tare={tareValue}
							used={usedArea}
							isOpen={!!openCards[path]}
							onToggle={() => handleToggleSpaceCard(path)}
							onTareChange={(value) => {
										handleTareChange(path, value);
									}}
								>
							{showLevelBreakdown ? (
								<div className={styles.levelSummary}>
									<div className={styles.levelSummaryTitle}>Level Breakdown</div>
									{overCapacityLevels.length > 0 ? (
										<div className={styles.levelSummaryWarning}>
											Over capacity:{' '}
											{overCapacityLevels
												.map(
													(entry) =>
														`Level ${entry.level} (${unitService.formatWithPreferredUnit(
															entry.remainingArea,
															'area',
															'm2'
														)})`
												)
												.join(', ')}
										</div>
									) : null}
									{levelSummaries.length > 0 ? (
										<ul className={styles.levelSummaryList}>
											{levelSummaries.map((entry) => {
												const capacity = levelCapacities.find(
													(item) => item.level === entry.level
												);
												const remainingArea = capacity?.remainingArea ?? 0;
												const remainingValueClass =
													remainingArea < 0
														? styles.levelMetricValueDanger
														: styles.levelMetricValueSuccess;

												if (useBasicGate1Breakdown) {
													return (
														<li key={`${path}-${card.functionId}-${entry.level}`}>
															<div className={styles.levelSummaryMain}>
																<span>
																	Level {entry.level} -{' '}
																	{card.functionName || 'Function'}
																</span>
																<span>
																	{unitService.formatWithPreferredUnit(
																		entry.area,
																		'area',
																		'm2'
																	)}
																</span>
															</div>
														</li>
													);
												}

												return (
													<li key={`${path}-${card.functionId}-${entry.level}`}>
														<div className={styles.levelSummaryMain}>
															<span>
																Level {entry.level} -{' '}
																{card.functionName || 'Function'}
															</span>
														</div>
														<div className={styles.levelSummaryMeta}>
															<MetricBox
																label="Gross Area"
																value={unitService.formatWithPreferredUnit(
																	capacity?.grossArea ?? entry.area,
																	'area',
																	'm2'
																)}
																className={styles.levelMetricBox}
															/>
															<MetricBox
																label="Net Usable Area"
																value={unitService.formatWithPreferredUnit(
																	capacity?.netArea ?? 0,
																	'area',
																	'm2'
																)}
																highlighted
																className={styles.levelMetricBox}
															/>
															<MetricBox
																label="Used Area"
																value={unitService.formatWithPreferredUnit(
																	capacity?.usedArea ?? 0,
																	'area',
																	'm2'
																)}
																className={styles.levelMetricBox}
															/>
															<MetricBox
																label="Remaining Area"
																value={unitService.formatWithPreferredUnit(
																	remainingArea,
																	'area',
																	'm2'
																)}
																className={styles.levelMetricBox}
																valueClassName={remainingValueClass}
															/>
														</div>
													</li>
												);
											})}
										</ul>
									) : (
										<div className={styles.levelSummaryEmpty}>
											No level breakdown available yet. Refresh after updating model
											functions.
										</div>
									)}
									{unassignedLevelUsed > 0 ? (
										<div className={styles.levelSummaryWarning}>
											Unassigned room usage:{' '}
											{unitService.formatWithPreferredUnit(
												unassignedLevelUsed,
												'area',
												'm2'
											)}
										</div>
									) : null}
								</div>
							) : null}

							{canEditRooms ? (
								<div className={styles.roomEntrySection}>
									<RoomType
										path={path}
										roomTypes={roomTypes}
										allowRoomEditing={canEditRooms}
										visibleLevels={visibleLevels}
									/>
								</div>
							) : null}
						</SpaceCard>
					</div>
				);
			})}
		</>
	);
};
